import ExcelJS from "exceljs";
import { PDFDocument, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";

const TH_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];

const periodLabel = (meta = {}) => {
  const y = meta.year ? (Number(meta.year) < 2500 ? Number(meta.year) + 543 : Number(meta.year)) : "";
  const m = meta.month ? TH_MONTHS[Number(meta.month) - 1] : "";
  return [m, y].filter(Boolean).join(" ");
};

const fileBase = (meta = {}) =>
  `AntHr-OT36-${meta.companyName || "ทุกบริษัท"}-${periodLabel(meta) || ""}`.replace(/\s+/g, "_");

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const fullName = (e) => `${e.firstname || ""} ${e.lastname || ""}`.trim();
const company = (e) => e.companyName || e.sectionName || "";

export const exportOT36Excel = async (weeks, meta = {}) => {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("OT เกิน 36 ชม.");

  const titleRow = ws.addRow([`พนักงานทำงานล่วงเวลาเกิน 36 ชม. ${periodLabel(meta)}`]);
  titleRow.font = { bold: true, size: 18, name: "TH SarabunPSK" };
  ws.mergeCells(1, 1, 1, 6);
  ws.addRow([]);

  const header = ws.addRow(["สัปดาห์", "ลำดับ", "รหัสพนักงาน", "ชื่อ - สกุล", "บริษัท / หน่วยงาน", "ชม. OT"]);
  const headerStyle = {
    font: { bold: true, size: 16, name: "TH SarabunPSK", color: { argb: "FFFFFF" } },
    alignment: { horizontal: "center", vertical: "middle" },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "002060" } },
  };
  header.eachCell((c) => (c.style = headerStyle));
  header.height = 32;
  [26, 8, 16, 34, 40, 12].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  const border = {
    top: { style: "thin", color: { argb: "D9D9D9" } },
    left: { style: "thin", color: { argb: "D9D9D9" } },
    bottom: { style: "thin", color: { argb: "D9D9D9" } },
    right: { style: "thin", color: { argb: "D9D9D9" } },
  };

  (weeks || []).forEach((wk) => {
    (wk.employees || []).forEach((e, idx) => {
      const row = ws.addRow([
        wk.label || "",
        idx + 1,
        e.employeeId || "",
        fullName(e),
        company(e),
        Number(e.amountHours) || 0,
      ]);
      row.eachCell((cell, col) => {
        cell.style = {
          font: { size: 15, name: "TH SarabunPSK" },
          alignment: { vertical: "middle", horizontal: col === 4 || col === 5 ? "left" : "center" },
        };
        cell.border = border;
        if (col === 6) cell.numFmt = "#,##0.00";
      });
    });
  });

  const buffer = await wb.xlsx.writeBuffer();
  triggerDownload(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `${fileBase(meta)}.xlsx`
  );
};

export const exportOT36Pdf = async (weeks, meta = {}) => {
  const base = process.env.REACT_APP_API_URL;
  const [regBytes, boldBytes] = await Promise.all([
    fetch(`${base}fonts/THSarabunNew.ttf`).then((r) => r.arrayBuffer()),
    fetch(`${base}fonts/THSarabunNew Bold.ttf`).then((r) => r.arrayBuffer()),
  ]);

  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);
  const font = await doc.embedFont(regBytes);
  const fontBold = await doc.embedFont(boldBytes);

  const W = 595.28;
  const H = 841.89;
  const M = 40;
  let page = doc.addPage([W, H]);
  let y = H - M;

  const ensureSpace = (need) => {
    if (y < M + need) {
      page = doc.addPage([W, H]);
      y = H - M;
    }
  };

  page.drawText("พนักงานทำงานล่วงเวลาเกิน 36 ชม.", { x: M, y, size: 20, font: fontBold });
  y -= 24;
  const sub = [meta.companyName, periodLabel(meta)].filter(Boolean).join(" · ");
  if (sub) {
    page.drawText(sub, { x: M, y, size: 14, font, color: rgb(0.45, 0.45, 0.45) });
    y -= 22;
  }

  (weeks || []).forEach((wk) => {
    const emps = wk.employees || [];
    if (!emps.length) return;

    ensureSpace(46);
    y -= 6;
    page.drawText(`${wk.label || ""} (${emps.length} คน)`, { x: M, y, size: 15, font: fontBold, color: rgb(0, 0.48, 1) });
    y -= 6;
    page.drawLine({ start: { x: M, y }, end: { x: W - M, y }, thickness: 0.5, color: rgb(0.82, 0.82, 0.82) });
    y -= 16;

    emps.forEach((e, i) => {
      ensureSpace(18);
      page.drawText(`${i + 1}. ${fullName(e)}`, { x: M, y, size: 14, font });
      const co = company(e);
      if (co) page.drawText(co, { x: M + 230, y, size: 11, font, color: rgb(0.55, 0.55, 0.55) });
      const hrs = `${Number(e.amountHours || 0).toFixed(2)} ชม.`;
      const hrsW = fontBold.widthOfTextAtSize(hrs, 14);
      page.drawText(hrs, { x: W - M - hrsW, y, size: 14, font: fontBold, color: rgb(0.9, 0.22, 0.21) });
      y -= 16;
    });
    y -= 8;
  });

  const bytes = await doc.save();
  triggerDownload(new Blob([bytes], { type: "application/pdf" }), `${fileBase(meta)}.pdf`);
};
